import { useEffect, useRef, useState } from 'react';

import { ChatMessage } from '../common/chat/types';

import ChatBox from './ChatBox';

export type TimedRevealChatBoxProps = {
  messages: ChatMessage[];
  location: string;
  height?: string;
};

export function TimedRevealChatBox(props: TimedRevealChatBoxProps) {
  const observerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [done, setDone] = useState(false);

  const [messages, setMessages] = useState<ChatMessage[]>([...props.messages.slice(0, 1)]);

  const [messageIdx, setMessageIdx] = useState(1);
  const [wordIdx, setWordIdx] = useState(0);
  const [optionIdx, setOptionIdx] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (!isVisible || done) {
        return;
      }
      setMessages((prevMessages) => {
        if (messageIdx < props.messages.length) {
          const message = props.messages[messageIdx];

          if (message.type !== 'text') {
            setMessageIdx((prevIdx) => prevIdx + 1);

            return [...prevMessages, message];
          }

          const words = message.message.split(' ');

          if (wordIdx === 0) {
            return [
              ...prevMessages,
              {
                message: words.slice(0, wordIdx + 1).join(' '),
                role: message.role,
                type: message.type,
              },
            ];
          } else if (wordIdx < words.length) {
            return [
              ...prevMessages.slice(0, -1),
              {
                message: words.slice(0, wordIdx + 1).join(' '),
                role: message.role,
                type: message.type,
              },
            ];
          }

          if (message.options && optionIdx < message.options.length) {
            // load in text options after the message
            setOptionIdx((prevIdx) => prevIdx + 1);

            return [
              ...prevMessages.slice(0, -1),
              {
                message: words.slice(0, wordIdx + 1).join(' '),
                role: message.role,
                type: message.type,
                options: message.options.slice(0, optionIdx + 1),
              },
            ];
          }
          setMessageIdx((prevIdx) => prevIdx + 1);
          setWordIdx(0);
          setOptionIdx(0);
        } else {
          setDone(true);

          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
        }

        return prevMessages;
      });

      setWordIdx((prevIdx) => prevIdx + 1);
    }, 30);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isVisible, done, messageIdx, wordIdx, optionIdx]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      { threshold: 0.1 },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, []);

  return (
    <ChatBox
      height={props.height}
      location={props.location}
      messages={messages}
      observerRef={observerRef}
      setMessages={setMessages}
    />
  );
}
